import { Controller } from "@hotwired/stimulus"

// Learner Sorting block (TMS-798): the author's row order is the correct
// answer (data-sorting-position), the rows are shuffled on connect, and the
// learner drags (or arrow-keys) them back into order. Check marks every row
// green/red and scores it; Try again clears the marks but keeps the learner's
// arrangement; Reset reshuffles. All state is client-side only — nothing is
// persisted.
export default class extends Controller {
  static targets = ["list", "item", "score", "checkButton", "resetButton", "completedBadge"]

  connect() {
    this.checked = false
    this.completed = false
    this.draggedItem = null
    this.shuffleItems()
  }

  startDrag(event) {
    if (this.checked || this.completed) {
      event.preventDefault()
      return
    }

    this.draggedItem = event.currentTarget
    this.draggedItem.classList.add("dragging")
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", "")
    }
  }

  endDrag() {
    this.draggedItem?.classList.remove("dragging")
    this.draggedItem = null
    this.renumberItems()
  }

  dragOver(event) {
    if (!this.draggedItem) return

    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move"

    const afterElement = this.itemAfterPointer(event.clientY)
    if (afterElement === this.draggedItem) return

    if (afterElement) {
      if (this.draggedItem.nextElementSibling !== afterElement) this.listTarget.insertBefore(this.draggedItem, afterElement)
    } else if (this.listTarget.lastElementChild !== this.draggedItem) {
      this.listTarget.appendChild(this.draggedItem)
    }
  }

  drop(event) {
    if (!this.draggedItem) return

    event.preventDefault()
    this.renumberItems()
  }

  moveItem(event) {
    if (this.checked || this.completed) return
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) return

    const item = event.currentTarget
    const sibling = event.key === "ArrowUp" ? item.previousElementSibling : item.nextElementSibling
    if (!sibling) return

    event.preventDefault()
    if (event.key === "ArrowUp") {
      sibling.before(item)
    } else {
      sibling.after(item)
    }
    this.renumberItems()
    item.focus()
  }

  checkOrRetry(event) {
    event.preventDefault()

    if (this.checked) {
      this.retry()
    } else {
      this.check()
    }
  }

  check() {
    const items = this.currentItems()
    const correctCount = items.filter((item, index) => Number(item.dataset.sortingPosition) === index).length
    const allCorrect = correctCount === items.length

    items.forEach((item, index) => {
      const correct = Number(item.dataset.sortingPosition) === index
      item.classList.toggle("is-correct", correct)
      item.classList.toggle("is-wrong", !correct)
      this.setItemResultText(item, correct ? "Correct position" : "Wrong position")
    })

    this.checked = true
    this.completed = allCorrect
    this.element.classList.add("is-checked")
    // Unhidden before the text lands so the live region is in the accessibility
    // tree when the score arrives, otherwise the announcement is dropped.
    this.scoreTarget.hidden = false
    this.scoreTarget.textContent = `${correctCount} of ${items.length} correct${allCorrect ? "!" : ""}`

    if (allCorrect) {
      this.element.classList.add("is-completed")
      this.checkButtonTarget.hidden = true
      this.completedBadgeTarget.hidden = false
    } else {
      this.checkButtonTarget.textContent = "Try again"
    }
  }

  // Clears the marks but keeps the learner's arrangement, so they only need
  // to fix the wrong rows.
  retry() {
    this.clearFeedback()
  }

  reset(event) {
    event.preventDefault()
    this.clearFeedback()
    this.shuffleItems()
  }

  clearFeedback() {
    this.checked = false
    this.completed = false
    this.element.classList.remove("is-checked", "is-completed")
    this.itemTargets.forEach((item) => {
      item.classList.remove("is-correct", "is-wrong")
      this.setItemResultText(item, "")
    })
    this.scoreTarget.hidden = true
    this.scoreTarget.textContent = ""
    this.checkButtonTarget.hidden = false
    this.checkButtonTarget.textContent = "Check Answer"
    this.completedBadgeTarget.hidden = true
  }

  shuffleItems() {
    const items = this.currentItems()
    if (items.length < 2) return

    for (let index = items.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1))
      ;[items[index], items[swapIndex]] = [items[swapIndex], items[index]]
    }

    // Never present the already-solved order: rotating by one guarantees a
    // different permutation without reshuffling in a loop.
    if (items.every((item, index) => Number(item.dataset.sortingPosition) === index)) {
      items.push(items.shift())
    }

    items.forEach((item) => this.listTarget.appendChild(item))
    this.renumberItems()
  }

  setItemResultText(item, text) {
    const result = item.querySelector("[data-sorting-item-result]")
    if (result) result.textContent = text
  }

  renumberItems() {
    this.currentItems().forEach((item, index) => {
      const number = item.querySelector("[data-sorting-item-number]")
      if (number) number.textContent = String(index + 1)
    })
  }

  itemAfterPointer(pointerY) {
    return this.currentItems().filter((item) => item !== this.draggedItem).reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = pointerY - box.top - (box.height / 2)

      if (offset < 0 && offset > closest.offset) return { offset, element: child }

      return closest
    }, { offset: Number.NEGATIVE_INFINITY, element: null }).element
  }

  currentItems() {
    return Array.from(this.listTarget.querySelectorAll(":scope > [data-sorting-target='item']"))
  }
}
